import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { IsaKpis, PenaltyBreakdown } from './IsaKpis';
import { TeamTable, PersonTable } from './IsaTables';
import { IsaDayDialog } from './IsaDayDialog';
import { IsaAuditTab } from './IsaAuditTab';
import { IsaTodayCard } from './IsaTodayCard';
import { IsaBacklogTab } from './IsaBacklogTab';
import { IsaSignoffTab } from './IsaSignoffTab';
import { IsaRulesTab } from './IsaRulesTab';

const istToday = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);

export default function InsideSalesAuditPage() {
  const [mode, setMode] = useState('month');
  const [month, setMonth] = useState(istToday().slice(0, 7));
  const [day, setDay] = useState(() => { const d = new Date(Date.now() + 5.5 * 3600e3 - 864e5); return d.toISOString().slice(0, 10); });
  const [tab, setTab] = useState('report');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDay, setOpenDay] = useState(null);
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);

  const params = mode === 'day' ? { day } : { month };
  const load = useCallback(() => {
    setLoading(true);
    return api.get('/inside-sales-audit/summary', { params: mode === 'day' ? { day } : { month } })
      .then(r => setData(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Could not load'))
      .finally(() => setLoading(false));
  }, [mode, day, month]);
  useEffect(() => { load(); }, [load]);

  const loadLive = async () => {
    setLiveLoading(true);
    try { setLive((await api.get('/inside-sales-audit/live')).data); } catch (e) { toast.error(e.response?.data?.detail || 'CRM read failed'); }
    finally { setLiveLoading(false); }
  };
  const exportXlsx = async () => {
    try {
      const r = await api.get('/inside-sales-audit/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `inside-sales-audit-${mode === 'day' ? day : month}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-5" data-testid="inside-sales-audit-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inside Sales Incentive Audit</h1>
          <p className="text-sm text-slate-500 mt-0.5">{(data?.employees || []).filter(e => !e.former).map(e => e.emp_id).join(' + ')} · team target {data?.rules.target ?? '…'} Hot/day · daily snapshot 11:59 PM IST from Zoho CRM</p>
        </div>
        <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="isa-export-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Export Excel</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="isa-filters">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList><TabsTrigger value="month" data-testid="isa-mode-month">Monthly</TabsTrigger><TabsTrigger value="day" data-testid="isa-mode-day">Daily</TabsTrigger></TabsList>
        </Tabs>
        {mode === 'month'
          ? <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white" data-testid="isa-month-input" />
          : <input type="date" value={day} max={istToday()} onChange={e => setDay(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white" data-testid="isa-day-input" />}
        <Tabs value={tab} onValueChange={setTab} className="ml-auto">
          <TabsList><TabsTrigger value="report" data-testid="isa-tab-report">Report</TabsTrigger>
            <TabsTrigger value="backlog" data-testid="isa-tab-backlog">Backlog</TabsTrigger>
            <TabsTrigger value="signoff" data-testid="isa-tab-signoff">Payout sign-off</TabsTrigger>
            <TabsTrigger value="rules" data-testid="isa-tab-rules">Rules</TabsTrigger>
            <TabsTrigger value="audit" data-testid="isa-tab-audit">Audit{data?.open_alerts ? ` (${data.open_alerts})` : ''}</TabsTrigger></TabsList>
        </Tabs>
      </div>

      {data?.open_alerts > 0 && tab === 'report' && (
        <button onClick={() => setTab('audit')} className="w-full text-left text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 flex items-center gap-2" data-testid="isa-alert-banner">
          <AlertTriangle className="w-3.5 h-3.5" />{data.open_alerts} open alert(s) — sync errors, missing snapshots or CRM changes. Open the Audit tab.</button>
      )}

      {loading && !data && <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>}

      {data && tab === 'report' && (
        <>
          <IsaKpis data={data} isDay={mode === 'day'} />
          <IsaTodayCard live={live} loading={liveLoading} onLoad={loadLive} rules={data.rules} />
          <TeamTable data={data} onOpen={setOpenDay} />
          <div className="grid lg:grid-cols-3 gap-4 items-start">
            <PenaltyBreakdown data={data} />
            {data.employees.map(e => <PersonTable key={e.key} data={data} emp={e} onOpen={setOpenDay} />)}
          </div>
          {data.missing_days.length > 0 && (
            <p className="text-xs text-slate-500" data-testid="isa-missing-note">{data.missing_days.length} past day(s) in this period have no snapshot and are not counted — back-fill them from the Audit tab.</p>
          )}
        </>
      )}
      {tab === 'backlog' && <IsaBacklogTab />}
      {tab === 'signoff' && <IsaSignoffTab month={mode === 'day' ? day.slice(0, 7) : month} onChanged={load} />}
      {tab === 'rules' && <IsaRulesTab />}
      {data && tab === 'audit' && <IsaAuditTab month={mode === 'day' ? day.slice(0, 7) : month} missing={data.missing_days} onChanged={load} />}

      <IsaDayDialog day={openDay} onClose={() => setOpenDay(null)} />
    </div>
  );
}
